<template>
  <JlcAppHeader
    v-bind="itemProps"
    ref="header"
    :class="isNavigationHidden ? 'hide-navigation' : ''"
    :home-link="isLoggedIn ? null : homeLink"
    @country-submit="handleLanguageCountrySubmit"
    @login="handleClickLogin"
    @signin="handleClickRegistration"
    @logout="handleLogout"
    @bookmark="handleBookmarkClick"
    @open-country-selector="handleOpenCountrySelector"
    @open-mobile-menu="handleOpenMobileMenu"
    @open-shop-overlay="openShopOverlay"
  >
    <template #before-logo>
      <HeaderMobileBackButton />
    </template>

    <template v-if="!shouldShowMobileVersion" v-slot:resume="{ labelClass }">
      <ResumeMenu :btn-class="labelClass" />
    </template>

    <template
      v-if="!shouldShowMobileVersion"
      name="header-dropdown-menu"
      v-slot:header-dropdown-menu="item"
    >
      <HeaderDropdownMenu :item="item" />
    </template>
  </JlcAppHeader>
</template>

<script>
import { mapGetters } from 'vuex';
import { bus } from '@/main';
import api from '@/api';
import { JlcAppHeader } from '@jobleads-frontend/ui_library';
import handleCountrySubmit from '@/helpers/countrySubmit';
import HeaderMobileBackButton from '@/components/App/HeaderMobileBackButton.vue';

import headerMobileMixin from '@/components/App/header-mobile-mixin';
import headerCountrySelectorMixin from '@/components/App/header-country-selector-mixin.js';
import headerBookmarkMixin from '@/components/App/header-bookmark-mixin';
import vuexDynamicModuleLoader from '@/mixin/vuex-dynamic-module-loader';
import deviceStore from '@/app/store/_device';

export default {
  name: 'AppHeader',
  mixins: [
    headerMobileMixin,
    headerCountrySelectorMixin,
    headerBookmarkMixin,
    vuexDynamicModuleLoader('device', deviceStore),
  ],
  components: {
    HeaderMobileBackButton,
    JlcAppHeader,
    ResumeMenu: () =>
      import(
        /* webpackChunkName: "header-d" */ '../../app/shared/components/Resume/ResumeMenu.vue'
      ),
    HeaderDropdownMenu: () => import(/* webpackChunkName: "header-d" */ './HeaderDropdownMenu.vue'),
  },
  computed: {
    ...mapGetters({
      navigationUrlList: 'navigationUrlList',
      isMdMax: 'breakpoints/isMdMax',
      user: 'user/info',
      getNumbers: 'user/getNumbers',
      isLoggedIn: 'user/isLoggedIn',
      isPremium: 'user/isPremium',
      language: 'language',
      languageOptions: 'languageOptions',
      country: 'country/currentCountry',
      regions: 'country/formattedRegions',
      usaQuiz: 'quiz/usaQuiz',
      usaReportHasBeenSeen: 'user/usaReportHasBeenSeen',
      getShopLink: 'getShopLink',
      useShopOverlayCampaign: 'useShopOverlayCampaign',
      isTablet: 'device/isTablet',
      isMasterClassAvailable: 'masterClass/isAvailable',
      masterClassLink: 'masterClass/link',
      countryBaseUrl: 'country/countryBaseUrl',
      resumeBuilderUserConfig: 'resumeBuilder/userConfig',
    }),

    homeLink() {
      return {
        link: this.countryBaseUrl.endsWith('/')
          ? this.countryBaseUrl.slice(0, -1)
          : this.countryBaseUrl,
        target: '_self',
      };
    },
    currentLanguage() {
      return this.languageOptions.find(({ value }) => this.language === value);
    },

    jlSourcePrefix() {
      return this.$route.meta?.jlSourcePrefix || this.$route.name;
    },
    isNavigationHidden() {
      return this.$route.meta?.hideNavigation || false;
    },
    modalParams() {
      return {
        query: {
          jl_source: `${this.jlSourcePrefix}_$device$_header`,
        },
      };
    },

    formattedUser() {
      if (!this.user) {
        return undefined;
      }

      return {
        id: this.user.id,
        name: this.user.name,
        lastName: this.user.surname,
        email: this.user.email,
        role: this.translate(this.user.role),
        premium: this.user.isPremium,
        image: this.user.image,
      };
    },
    helpLink() {
      return this.navigationUrlList.support;
    },
    mainLinks() {
      const { path } = (this.$route.matched || [])[0] || {};

      const jobs = (() => {
        const link = '/search/jobs';
        const result = {
          label: this.translate('Jobs'),
          link,
          active: [link, '/search/jobs/hh/:headhunterId'].includes(path),
          target: '_self',
        };
        return {
          ...result,
        };
      })();

      const headhunter = (() => {
        const link = this.isLoggedIn ? '/search/headhunter' : `${this.countryBaseUrl}headhunters`;

        const result = {
          label: this.translate('Headhunters'),
          link,
          active: [link, '/search/headhunter'].includes(path),
          target: this.isLoggedIn ? undefined : '_self',
        };
        return {
          ...result,
        };
      })();

      return [
        {
          label: this.translate('Home'),
          link: '/home',
          visible: this.isLoggedIn,
          target: '_self',
        },
        jobs,
        headhunter,
        this.resumeMenuItem,
        this.coachingMenuItem,
      ].filter((option) => option.visible !== false);
    },
    coachingMenuItem() {
      const { translate, isLoggedIn } = this;
      const menu = {
        label: translate('Coaching'),
        slotName: 'header-dropdown-menu', // for desktop
        icon: 'play-circle-filled',
        options: [
          {
            label: 'Career Advice',
            link: '/career-advice',
            target: '_self',
          },
          {
            label: translate('MasterClass'),
            link: this.masterClassLink,
            visible: this.isMasterClassAvailable,
            target: '_self',
          },
        ],
        expanded: false,
        active: ['/my-guides'].includes(this.$route.path),
      };
      if (isLoggedIn) {
        menu.options.push({
          label: translate('Career Guides'),
          link: '/my-guides',
        });
      }

      return menu;
    },
    extraLinks() {
      const links = [
        { component: 'countrySelector' },
        {
          label: this.translate('Help'),
          link: this.helpLink,
          target: '_blank',
          rel: 'noreferrer',
        },
      ];
      // if (!this.isLoggedIn) {
      //   links.unshift({
      //     label: this.translate('Customer Reviews'),
      //     link: `${this.countryBaseUrl}reviews`,
      //     target: '_self',
      //   });
      // }

      return links;
    },

    discoverPremiumLink() {
      if (!this.isLoggedIn || this.isPremium) return undefined;

      if (this.useShopOverlayCampaign && !window.appSetup.user.canUseNewShop) {
        return {
          label: this.translate('Discover Premium'),
          action: 'open-shop-overlay',
        };
      }

      if (window.appSetup.user.canUseNewShop) {
        return {
          label: this.translate('Discover Premium'),
          link: `/new/shop?returnUrl=${encodeURIComponent(this.$route.path)}`,
          target: '_self',
        };
      }

      return {
        label: this.translate('Discover Premium'),
        target: '_self',
        link: this.getShopLink('header'),
        menuLink: this.getShopLink('menu'),
      };
    },
    myGuidesMenuItem() {
      return {
        label: this.translate('My guides'),
        link: '/my-guides',
        icon: 'file-document',
        visible: this.isLoggedIn,
      };
    },
    hasAssessmentReport() {
      return this.isLoggedIn && !!this.usaQuiz?.reportId;
    },
    userHasNotSeenUsaReport() {
      return (
        this.hasAssessmentReport &&
        !!(this.usaQuiz?.hasSeenReport !== true && !this.usaReportHasBeenSeen)
      );
    },
    assessmentReportMenuItem() {
      return {
        label: this.translate('My Assessment'),
        icon: 'certificate',
        link: '/self-assessment-report',
        marked: this.userHasNotSeenUsaReport,
        visible: this.hasAssessmentReport,
        target: '_self',
      };
    },

    profileLinks() {
      return [
        {
          label: this.translate('My account'),
          link: '/account',
          icon: 'cog',
        },
        this.assessmentReportMenuItem,
        {
          label: this.translate('Support and feedback'),
          link: this.helpLink,
          icon: 'help-circle',
          target: '_blank',
        },
        this.myGuidesMenuItem,
        {
          label: this.translate('Log out'),
          action: 'logout',
          icon: 'logout',
        },
      ].filter((option) => option.visible !== false);
    },
    resumeMenuItem() {
      const links = {
        label: this.translate('Resume'),
        slotName: 'resume', // for desktop
        icon: 'paper-check',
        options: [],
        expanded: false,
        active: false,
      };
      if (this.resumeBuilderUserConfig && this.resumeBuilderUserConfig.enabled) {
        links.options.push({
          label: this.translate('Resume builder'),
          link: '/resume-builder?originPage=general&originCta=main_nav_resume_builder',
          target: '_self',
        });
      }
      links.options.push({
        label: this.translate('Free review'),
        link: this.isLoggedIn
          ? `/free-resume-review/report`
          : `${this.countryBaseUrl}free-resume-review`,
        target: '_self',
      });
      if (this.resumeBuilderUserConfig && this.resumeBuilderUserConfig.enabled) {
        links.options.push({
          label: this.translate('My documents'),
          link: '/my-documents',
          target: '_self',
        });
      }
      return links;
    },

    itemProps() {
      return {
        mainLinks: this.mainLinks,
        extraLinks: this.extraLinks,
        profileLinks: this.profileLinks,
        discoverPremiumLink: this.discoverPremiumLink,
        mobileLinks: this.mobileLinks,
        bookmarkCount: this.bookmarkCount,

        forceMobile: this.isTablet,
        isMobile: this.shouldShowMobileVersion,

        language: this.currentLanguage,
        languageOptions: this.languageOptions,

        country: this.country,
        regions: this.regions,

        user: this.formattedUser,
      };
    },
  },

  provide() {
    return {
      async uploadAvatar(formData) {
        const { dispatch } = this.$store;

        const { data } = await api.file('/upload-file', formData);

        if (data?.status !== 'OK') {
          throw new Error('Response data status is not ok');
        }

        return dispatch('user/fetch');
      },
      async deleteAvatar() {
        const { dispatch } = this.$store;
        try {
          await api.delete(this.$store.getters.getApiLink('user.deleteAvatar'));

          return dispatch('user/fetch');
        } catch {
          throw new Error('Response data status is not ok');
        }
      },
    };
  },
  methods: {
    handleLanguageCountrySubmit({ language, country }) {
      if (
        this.$route.query.location_country &&
        this.$route.query.location_country !== country.value
      ) {
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            location_country: country.value,
          },
        });
      }
      handleCountrySubmit({ language, country });
    },

    handleClickLogin() {
      bus.$emit('open-modal', 'login', this.modalParams);
      this.$refs.header.setMobileMenuActivity(false);
    },
    handleClickRegistration() {
      bus.$emit('open-modal', 'registration', this.modalParams);
      this.$refs.header.setMobileMenuActivity(false);
    },
    handleLogout() {
      const { dispatch } = this.$store;
      dispatch('user/clearLoggedUserData').then(() => dispatch('user/logout'));
    },
    handleOpenMobileMenu() {
      // this.$store.dispatch('categoryTree/fetchTopMenu');
      this.$store.dispatch('country/getCountriesWithGeoSupport');
    },
    handleOpenCountrySelector() {
      this.$store.dispatch('country/getCountriesWithGeoSupport');
    },
    openShopOverlay() {
      this.$store.commit('setShowShopOverlay', true);
      this.$store.commit('setShopOverlayLink', 'discover-premium-link');
    },
  },
};
</script>
